import React, { useState, useEffect, useCallback } from 'react';
import { adminGet, adminDelete } from '../api';
import ConfirmDialog from './ConfirmDialog';
import MovieForm     from './MovieForm';

export default function MovieList() {
  const [movies,   setMovies]   = useState([]);
  const [genres,   setGenres]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState('');
  const [recentOnly, setRecentOnly] = useState(false);
  const [editing,  setEditing]  = useState(null);   // movie object
  const [adding,   setAdding]   = useState(false);
  const [delTarget, setDelTarget] = useState(null); // movie to confirm delete

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [movieData, genreData] = await Promise.all([
        adminGet('/api/admin/movies'),
        adminGet('/api/admin/movie-genres'),
      ]);
      setMovies(movieData);
      setGenres(genreData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    await adminDelete(`/api/admin/movies/${delTarget.id}`);
    setDelTarget(null);
    load();
  };

  const handleSave = () => {
    setEditing(null);
    setAdding(false);
    load();
  };

  const genreMap = Object.fromEntries(genres.map(g => [g.id, g.name]));

  const visible = movies.filter(m => {
    if (recentOnly && !m.is_recently_added) return false;
    if (search && !m.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (editing) {
    return (
      <div>
        <h2 className="admin-page-title">Movies</h2>
        <MovieForm movie={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  if (adding) {
    return (
      <div>
        <h2 className="admin-page-title">Movies</h2>
        <MovieForm onSave={handleSave} onCancel={() => setAdding(false)} />
      </div>
    );
  }

  return (
    <div>
      <h2 className="admin-page-title">Movies</h2>

      <div className="admin-action-bar">
        <button className="btn btn-primary" onClick={() => setAdding(true)}>+ Add movie</button>
        <input
          className="admin-search"
          placeholder="Search by title…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <label className="admin-checkbox-row" style={{ fontSize: 13 }}>
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={e => setRecentOnly(e.target.checked)}
          />
          Recently added only
        </label>
        <button className="btn btn-secondary" onClick={load}>Refresh</button>
      </div>

      {error   && <div className="admin-banner admin-banner-error">{error}</div>}
      {loading && <div style={{ color: '#888' }}>Loading…</div>}

      {!loading && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Genre</th>
              <th>Recently added</th>
              <th>Sort</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={5} style={{ color: '#888', textAlign: 'center' }}>No movies found</td></tr>
            ) : visible.map(m => (
              <tr key={m.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{m.title}</div>
                  {m.thumbnail && (
                    <img src={m.thumbnail} alt="" style={{ height: 36, marginTop: 4, borderRadius: 3 }} />
                  )}
                </td>
                <td>{genreMap[m.genre_id] ?? m.genre_id}</td>
                <td>{m.is_recently_added ? '✓' : ''}</td>
                <td>{m.sort_order}</td>
                <td>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditing(m)}>Edit</button>
                    <button className="btn btn-sm btn-danger"    onClick={() => setDelTarget(m)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={Boolean(delTarget)}
        title={`Delete "${delTarget?.title}"?`}
        message="This movie will be permanently removed."
        onConfirm={handleDelete}
        onCancel={() => setDelTarget(null)}
      />
    </div>
  );
}
